import { useSpring, animated } from 'react-spring';
import { ReactNode, useState, useEffect, useRef } from 'react';

interface ParallaxSectionProps {
    children: ReactNode;
    className?: string;
    delay?: number;
    speed?: number; // Parallax speed multiplier (0.1 = slow, 0.5 = fast)
    noSlide?: boolean; // Disable slide animation (just fade in)
    noParallax?: boolean; // Disable parallax movement entirely
}

export default function ParallaxSection({
    children,
    className = '',
    delay = 0,
    speed = 0.3,
    noSlide = false,
    noParallax = false
}: ParallaxSectionProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [parallaxOffset, setParallaxOffset] = useState(0);
    const domRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Keep observing for parallax effect
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        });

        const currentElement = domRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    // Parallax scroll effect - only on inner content
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!domRef.current) return;

            const rect = domRef.current.getBoundingClientRect();
            const elementTop = rect.top;
            const windowHeight = window.innerHeight;

            // Only calculate parallax when element is in viewport
            if (elementTop < windowHeight && elementTop > -rect.height) {
                if (!ticking) {
                    window.requestAnimationFrame(() => {
                        // Calculate parallax offset based on viewport position
                        const scrollProgress = (windowHeight - elementTop) / (windowHeight + rect.height);
                        // More aggressive parallax: -50 to +50 range
                        const offset = (scrollProgress - 0.5) * 100 * speed;
                        setParallaxOffset(offset);
                        ticking = false;
                    });
                    ticking = true;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial calculation

        return () => window.removeEventListener('scroll', handleScroll);
    }, [speed]);

    // Fade and slide animation for outer container
    const fadeSlideAnimation = useSpring({
        opacity: isVisible ? 1 : 0,
        transform: noSlide ? 'translateY(0)' : (isVisible ? 'translateY(0)' : 'translateY(20px)'),
        delay: delay,
        config: { tension: 120, friction: 30 }
    });

    // Parallax animation for inner content
    const parallaxAnimation = useSpring({
        transform: (isVisible && !noParallax) ? `translateY(${parallaxOffset}px)` : 'translateY(0px)',
        config: { tension: 200, friction: 50 }
    });

    return (
        <div ref={domRef} className={className}>
            <animated.div style={fadeSlideAnimation}>
                <animated.div ref={innerRef} style={parallaxAnimation}>
                    {children}
                </animated.div>
            </animated.div>
        </div>
    );
}
